/**
 * KYC Import Configuration
 *
 * Defines how the FrankieOne Country Coverage xlsx maps to our DB tables.
 * Header-based matching (resilient to column reordering).
 */

export type BoolNormMode = 'Y' | 'x' | 'YesY'

export interface ColumnMapping {
  header: string
  field: string
  type: 'string' | 'number' | 'boolean' | 'skip'
  boolMode?: BoolNormMode
  optional?: boolean
}

export interface SheetConfig {
  sheetName: string
  headerRowOffset: number
  positional?: boolean
  positionalDataStart?: number
  positionalColumns?: Array<{ index: number; field: string; type: 'string' | 'boolean'; boolMode?: BoolNormMode }>
  columns: ColumnMapping[]
}

// ─── Sheet Configs ─────────────────────────────────────────────────────

export const SUMMARY_CONFIG: SheetConfig = {
  sheetName: 'SUMMARY',
  headerRowOffset: 0,
  positional: true,
  positionalDataStart: 17,
  positionalColumns: [
    { index: 1, field: 'country_name', type: 'string' },
    { index: 2, field: 'idv', type: 'boolean', boolMode: 'Y' },
    { index: 3, field: 'kyc_one_plus', type: 'boolean', boolMode: 'Y' },
    { index: 4, field: 'kyc_two_plus', type: 'boolean', boolMode: 'Y' },
    { index: 5, field: 'kyc_gov_id', type: 'boolean', boolMode: 'Y' },
    { index: 6, field: 'kyb_profile', type: 'boolean', boolMode: 'Y' },
    { index: 7, field: 'kyb_registry_docs', type: 'boolean', boolMode: 'Y' },
    { index: 8, field: 'kyb_ownership', type: 'boolean', boolMode: 'Y' },
    { index: 9, field: 'aml', type: 'boolean', boolMode: 'Y' },
  ],
  columns: [],
}

export const KYC_SUMMARY_CONFIG: SheetConfig = {
  sheetName: 'KYC Summary',
  headerRowOffset: 4,
  columns: [
    { header: 'Country', field: 'country_name', type: 'string' },
    { header: 'Region', field: 'region', type: 'string' },
    { header: 'Verification Type', field: 'verification_type', type: 'string' },
    { header: 'Data Sources', field: 'data_source_count', type: 'number' },
    { header: 'Coverage Estimate', field: 'coverage_estimate', type: 'number' },
    { header: 'Average Match Rate', field: 'avg_match_rate', type: 'number' },
    { header: 'Average Response Time (s)', field: 'avg_response_time', type: 'string' },
  ],
}

export const KYC_CONFIG: SheetConfig = {
  sheetName: 'KYC',
  headerRowOffset: 4,
  columns: [
    { header: 'Country', field: 'country', type: 'string' },
    { header: 'Vendor', field: 'vendor', type: 'string' },
    { header: 'Purpose', field: 'purpose', type: 'string' },
    { header: 'Data Source Header', field: 'data_source_header', type: 'string' },
    { header: 'Description', field: 'description', type: 'string' },
    { header: 'First name', field: 'has_first_name', type: 'boolean', boolMode: 'x' },
    { header: 'Middle name', field: 'has_middle_name', type: 'boolean', boolMode: 'x' },
    { header: 'Last name', field: 'has_last_name', type: 'boolean', boolMode: 'x' },
    { header: 'DOB', field: 'has_dob', type: 'boolean', boolMode: 'x' },
    { header: 'Gender', field: 'has_gender', type: 'boolean', boolMode: 'x' },
    { header: 'Address', field: 'has_address', type: 'boolean', boolMode: 'x' },
    { header: 'National ID', field: 'has_national_id', type: 'boolean', boolMode: 'x' },
    { header: 'Phone number', field: 'has_phone', type: 'boolean', boolMode: 'x' },
    { header: 'Government ID', field: 'government_id', type: 'boolean', boolMode: 'YesY' },
    { header: 'Consent required?', field: 'consent_required', type: 'boolean', boolMode: 'YesY' },
    { header: 'Mandatory PII', field: 'mandatory_pii', type: 'string' },
    { header: 'Verifiable Elements', field: 'verifiable_elements', type: 'string' },
    { header: 'Optional Elements', field: 'optional_elements', type: 'string' },
    { header: 'API - v1/v2/ both', field: 'api_version', type: 'string' },
    { header: 'F1 Deal Sheet', field: 'f1_deal_sheet', type: 'string' },
    { header: 'Notes', field: 'notes', type: 'string' },
    { header: 'Adult Coverage', field: 'adult_coverage', type: 'number' },
    { header: 'National ID Name', field: 'national_id_name', type: 'string', optional: true },
    { header: 'National ID Format', field: 'national_id_format', type: 'string', optional: true },
    { header: 'National ID Example', field: 'national_id_example', type: 'string', optional: true },
    { header: 'National ID Regex', field: 'national_id_regex', type: 'string', optional: true },
  ],
}

export const RECOMMENDATIONS_CONFIG: SheetConfig = {
  sheetName: 'KYC Recommendations',
  headerRowOffset: 7,
  columns: [
    { header: 'Country', field: 'country', type: 'string' },
    { header: 'ISO Alpha-3', field: 'iso_alpha3', type: 'string' },
    { header: 'Available Vendors', field: 'available_vendors', type: 'string' },
    { header: 'Recommended Provider', field: 'recommended_vendor', type: 'string' },
    { header: 'Recommended Default?', field: 'is_recommended_default', type: 'boolean', boolMode: 'YesY' },
    { header: 'Est. Sell Price', field: 'sell_price', type: 'skip' },
    { header: 'API Status', field: 'api_status', type: 'string' },
    { header: 'Govt ID Available', field: 'gov_id_available', type: 'boolean', boolMode: 'YesY' },
    { header: 'Notes / Multi-vendor Comparison', field: 'notes', type: 'string' },
  ],
}

export const ALL_SHEET_CONFIGS = [SUMMARY_CONFIG, KYC_SUMMARY_CONFIG, KYC_CONFIG, RECOMMENDATIONS_CONFIG]

// ─── Skip rules ────────────────────────────────────────────────────────

export const SKIP_NAMES = new Set(['nan', 'References', '', 'None', 'undefined'])
export const SKIP_PREFIXES = ['Connector times', 'Response times']

export function shouldSkipRow(name: string): boolean {
  if (!name) return true
  if (SKIP_NAMES.has(name)) return true
  return SKIP_PREFIXES.some(p => name.startsWith(p))
}

// ─── Boolean normalization ─────────────────────────────────────────────

export function normalizeBoolean(val: unknown, mode: BoolNormMode): boolean {
  if (val == null) return false
  const s = String(val).trim()
  if (!s) return false
  switch (mode) {
    case 'Y': return s.toUpperCase() === 'Y'
    case 'x': return s.toLowerCase() === 'x'
    case 'YesY': return ['yes', 'y', 'true'].includes(s.toLowerCase())
  }
}

// ─── Number parsing ────────────────────────────────────────────────────

export function parseNumber(val: unknown): number | null {
  if (val == null) return null
  const s = String(val).trim().replace(/[*%s]/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return isNaN(n) ? null : n
}

// ─── Header matching ───────────────────────────────────────────────────

export function normalizeHeader(h: string): string {
  return h.trim().replace(/\s+/g, ' ').toLowerCase()
}
